import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'

import PageWrapper from '@Layout/PageWrapper'
import Layout      from '@Layout/BackOffice'

import CheckboxEl  from '@/Form/V2/Managed/CheckboxEl'
import ModelPicker from '@/Form/V2/ModelPicker'
import PartnerRow  from '@/Lists/Rows/PartnerRow'
import Dashboard   from '@/Lists/Dashboard'
import Select      from '@/Form/V2/Select'

import bemit from '@function/bemit'

import SearchBar from '@Pure/SearchBar'
import Link      from '@Pure/Link'

import SetPartnersRenewalModal from '@Modal/SetPartnersRenewalModal'

import { renewal_status } from '@Config/options'

import { isTrue, num, getRenewalYear } from '@root/utility'

import DashboardStore from '@Store/Dashboard'

const DASHBOARD = 'PartnersList';

const renewal_options =
[
	{ value: 'all', label: 'Tout' }
]
.concat(
	Object.keys(renewal_status)
	.map(val => ({ value: val, label: renewal_status[val]}))
);

const sortable_columns = [];

const fields_with_timeout =
[
	'searched'
];

const storage_filters =
{
	searched: window.localStorage.getItem(DASHBOARD + '.searched') || '',
	category: window.localStorage.getItem(DASHBOARD + '.category') || '',
	enabled: isTrue(window.localStorage.getItem(DASHBOARD + '.enabled')),
	highlighted: isTrue(window.localStorage.getItem(DASHBOARD + '.highlighted')),
	renewal_status: window.localStorage.getItem(DASHBOARD + '.renewal_status') || 'all',

};

const default_header =
[
	{
		label: '', name: '_checkbox'
	},
	{
		label: 'Nom', name: 'name'
	},
	{
		label: 'Catégorie', name: 'category'
	},
	{
		label: 'Tél.', name: 'phone'
	},
	{
		label: 'Adresse', name: 'city'
	},
	{
		label: 'Mis en avant', name: 'highlighted'
	},
	{
		label: 'Renouvelé', name: 'renewal'
	},
	{
		label: '', name: 'actions'
	}
]
.map(opt => ({ ...opt, sortable: sortable_columns.includes(opt.name) }));

const default_fields = default_header.map(data => data.name);

function getHeader (fields=[])
{
	return default_header.filter(({ name }) => fields.includes(name));
}

export default PageWrapper(function PartnersV2Page ({ ...props })
{
	const search_timeout = useRef();
	const [ids, setIds] = useState();
	const [modal, setModal] = useState({ });
	const [fields, setFields] = useState(default_fields);
	const [header, setHeader] = useState(default_header);

	useEffect(function ()
	{
		setHeader(getHeader(fields));
	},
	[fields]);

	useEffect(function ()
	{
		DashboardStore.update(function (store)
		{
			store[DASHBOARD] ||= { };
			store[DASHBOARD].cbs = { };
		});
	},
	[]);

	useEffect(function ()
	{
		if (!ids) return () => void 0;

		DashboardStore.update(function (store)
		{
			store[DASHBOARD] ||= { };

			const cbs = ids.reduce(function (cbs, id)
			{
				if (isTrue(cbs[id])) cbs[id] = true;

				return cbs;
			},
			{ });

			store[DASHBOARD].cbs = cbs;
		});
	},
	[ids]);

	const openModal = useCallback(function (e)
	{
		setModal({ open: true, trg: e?.target });
	},
	[]);

	const closeModal = useCallback(function (e)
	{
		setModal({ });
	},
	[]);

	const selectAll = useCallback(function (e)
	{
		DashboardStore.update(function (store)
		{
			store[DASHBOARD] ||= { };

			store[DASHBOARD].cbs = ids.reduce(function (obj, id)
			{
				obj[id] = true;

				return obj;
			},
			{ });
		});
	},
	[ids]);

	const unSelectAll = useCallback(function (e)
	{
		DashboardStore.update(function (store)
		{
			store[DASHBOARD] ||= { };
			store[DASHBOARD].cbs = { };
		});
	},
	[]);

	const handleFetched = useCallback(function ({ response, data, count, results_for })
	{
		setIds(data.map(doc => doc.id));
	},
	[]);

	const [instant_filters, setInstantFilters] = useState(
	{
		...storage_filters, renewal_year: getRenewalYear()
	});

	useEffect(function ()
	{
		const status = !instant_filters.renewal_status
		? 'all'
		: instant_filters.renewal_status;

		if (['stopped', 'renewed'].includes(status))
		{
			const fields = default_fields.filter(name => name !== '_checkbox');

			setFields(fields);
			setHeader(getHeader(fields));
		}
		else
		{
			setFields(default_fields);
			setHeader(default_header);
		}
	},
	[instant_filters]);

	const [delayed_filters, setDelayedFilters] = useState({ ...storage_filters });

	const renewal_years = useMemo(function ()
	{
		const RENEWAL_YEAR = getRenewalYear();

		const options = Array.from({ length: num(RENEWAL_YEAR, 0) - 2020 })
		.map(function (_v, i)
		{
			const YEAR = String(num(RENEWAL_YEAR, 0) - i);

			return { label: String(num(YEAR, 0) + 1), value: YEAR };
		});

		return options;
	},
	[]);

	const handleChange = useCallback(function ({ value, e, name })
	{
		if (!name) return;

		if (e === null && fields_with_timeout.includes(name) === false) return;

	// Prevent on mount trigger
		if (e === null && fields_with_timeout.includes(name)) return;

		window.localStorage.setItem(DASHBOARD + '.' + name, value);

		setInstantFilters(function (filters)
		{
			return { ...filters, [name]: value };
		});

		if (fields_with_timeout.includes(name) === false) return;

		if (search_timeout.current)
		{
			window.clearTimeout(search_timeout.current);
		}

		search_timeout.current = window.setTimeout(function ()
		{
			search_timeout.current = undefined;

			setDelayedFilters(function (filters)
			{
				return { ...filters, [name]: value };
			});
		},
		320);
	},
	[]);

	const handleSearchChange = useCallback(function (value, data={ }, e)
	{
		handleChange({ value: value, name: data?.name, e });
	},
	[handleChange]);

	const handleNativeChange = useCallback(function (e)
	{
		handleChange({ value: e?.target?.value, name: e?.target?.name, e });
	},
	[handleChange]);

	const handleSwitchChange = useCallback(function (e, name)
	{
		handleChange(
		{
			e: undefined,
			value: !isTrue(instant_filters[name]),
			name: name
		});
	},
	[handleChange, instant_filters]);

	const filters = useMemo(function ()
	{
		const filters =
		{
			searched: delayed_filters.searched,
			category: instant_filters.category,
			enabled: instant_filters.enabled,
			highlighted: instant_filters.highlighted,
			renewal_status: instant_filters.renewal_status,
			renewal_year: instant_filters.renewal_year
		};

		return filters;
	},
	[instant_filters, delayed_filters]);

	const set_renewals_buttons = ['stopped', 'renewed']
	.includes(instant_filters.renewal_status) ? undefined :
	(
		<div className="u-flex u-full-width u-gap-20 u-margin-bottom-10">

			<button type="button" onClick={ selectAll }
				className={ bemit('c-btn', ['v2', 'smaller']) }>
				Tout cocher
			</button>

			<button type="button" onClick={ unSelectAll }
				className={ bemit('c-btn', ['v2', 'smaller']) }>
				Tout décocher
			</button>

			<button type="button" onClick={ openModal }
				className={ bemit('c-btn', ['v2', 'smaller']) }
				disabled={ !ids || ids.length > 0 === false }>
				Marquer comme renouvelé
			</button>

		</div>
	);

	return (
		<Layout page_title="Partenaires" history={ props.history } hide_title>

			<div className="page-content">

				<div className="o-grid-4 u-flex-wrap u-content-between
					u-margin-bottom-15 u-items-end dashboard-filters-grid">

					<div className="o-grid__item-4">
						<SearchBar hide_btn={ true }
							placeholder="Recherche (nom)"
							handleChange={ handleSearchChange }
							value={ instant_filters.searched }
							name="searched"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item-8 u-flex u-gap-20 u-items-end">

						<CheckboxEl name="enabled"
							checked={ isTrue(instant_filters.enabled) }
							onChange={ handleSwitchChange }
							label="Activés"
							left-label="Désactivés"
							bemit={ ['switch', 'filter-switch'] }
						/>

						<ModelPicker onChange={ handleChange }
							name="category"
							value={ instant_filters.category }
							label="Catégorie"
							model="category"
							url="/autocomplete/categories"
							bemit={ ['25%'] }
							resetable
						/>

						<Select onChange={ handleNativeChange }
							label="Renouvellement"
							value={ instant_filters.renewal_status }
							name="renewal_status"
							options={ renewal_options }
							bemit={ ['auto'] }
						/>

						<Select onChange={ handleNativeChange }
							label="Année"
							value={ instant_filters.renewal_year }
							name="renewal_year"
							options={ renewal_years }
							className="renewal-year-filter"
						/>

						<CheckboxEl name="highlighted"
							checked={ isTrue(instant_filters.highlighted) }
							onChange={ handleSwitchChange }
							label="En avant"
							bemit={ ['switch', 'filter-switch'] }
						/>

					</div>

				</div>

				<div className="o-content">
					<div className="u-flex u-content-end u-margin-bottom-20">
						<Link href="/gestion-du-catalogue/nouveau-partenaire"
							className="c-btn c-btn--add u-no-margin-top">
							Nouveau partenaire
						</Link>
					</div>

					{ set_renewals_buttons }

				</div>

				<div className="o-content">
					<Dashboard model="Partner" items-per-page={ 50 }
						url="/dashboard/partners" filters={ filters }
						header={ header } fields={ fields }
						order="ASC" order-by="name"
						disable-sort={ filters.searched ? true : false }
						component={ PartnerRow }
						onFetched={ handleFetched }
						request-update="updatePartnersList"
					/>
				</div>
			</div>

			<SetPartnersRenewalModal open={ modal?.open === true }
				onClose={ closeModal } bemit={ ['intermediate'] }
			/>

		</Layout>
	);
});